import { api } from "@/lib/api";
import type { ApiKey } from "@/types/admin";

export interface ApiKeysListResponse {
	data: ApiKey[];
	meta: {
		page: number;
		limit: number;
		total: number;
		totalPages: number;
		hasNext: boolean;
		hasPrev: boolean;
	};
}

export interface ApiKeysFilters {
	page?: number;
	limit?: number;
	userId?: string;
	projectId?: string;
	status?: "active" | "inactive";
	search?: string;
	isBlocked?: boolean;
}

// API Keys API Service
export const apiKeysService = {
	// Get paginated list of API keys
	getApiKeys: async (
		page = 1,
		limit = 50,
		filters?: Omit<ApiKeysFilters, "page" | "limit">,
	): Promise<ApiKeysListResponse> => {
		const params = {
			page,
			limit,
			...filters,
		};

		const response = await api.get<ApiKeysListResponse>(
			"/admin/api-keys",
			params,
		);
		if (!response.data.success || !response.data.data) {
			throw new Error("Failed to fetch API keys");
		}
		return response.data.data;
	},

	// Get API key by ID
	getApiKey: async (apiKeyId: string): Promise<ApiKey> => {
		const response = await api.get<ApiKey>(`/admin/api-keys/${apiKeyId}`);
		if (!response.data.success || !response.data.data) {
			throw new Error("Failed to fetch API key");
		}
		return response.data.data as ApiKey;
	},

	// Revoke API key
	revokeApiKey: async (apiKeyId: string): Promise<ApiKey> => {
		const response = await api.post<ApiKey>(
			`/admin/api-keys/${apiKeyId}/revoke`,
		);
		if (!response.data.success || !response.data.data) {
			throw new Error("Failed to revoke API key");
		}
		return response.data.data as ApiKey;
	},

	// Block API key
	blockApiKey: async (apiKeyId: string, reason: string): Promise<ApiKey> => {
		const response = await api.post<ApiKey>(
			`/admin/api-keys/${apiKeyId}/block`,
			{ reason },
		);
		if (!response.data.success || !response.data.data) {
			throw new Error("Failed to block API key");
		}
		return response.data.data as ApiKey;
	},
};
